import { useState, useEffect, useCallback, useMemo } from 'react';
import { getUsers } from '../api/users';
import type { UserDto } from '../types';

interface UseUsersOptions {
  isActive?: boolean;
}

/**
 * Hook for fetching and managing users list
 */
export function useUsers(options?: UseUsersOptions) {
  const [data, setData] = useState<UserDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Memoize options to avoid unnecessary re-renders
  const optionsKey = useMemo(() => JSON.stringify(options), [options]);

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const users = await getUsers(options?.isActive);
      setData(users);
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      setError(error.response?.data?.message || 'Failed to fetch users');
    } finally {
      setIsLoading(false);
    }
  }, [options?.isActive]);

  useEffect(() => {
    fetchUsers();
  }, [optionsKey, fetchUsers]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchUsers,
  };
}
